import { ApolloQueryResult, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import {
  getDoctorPatientTreatments,
  getDoctorPatientTreatmentsVariables,
  TreatmentStatus,
} from '../../../../models/graphqlTypes'
import { GET_DOCTOR_PATIENT_TREATMENTS } from '../../../../operations/doctorPatientOperations'
import {
  DoctorPatientTreatment,
  TreatmentListFilter,
} from '../types/treatments.types'
import dayjs from 'dayjs'
import { useSelectedLanguage } from '../../../../common/hooks/useSelectedLanguage'
import { useMe } from '../../../../common/hooks/useMe'
import { DoctorPatientRouteParams } from '../../DoctorPatients/types/doctorPatients.types'

type Props = {
  skip?: boolean
  patientId?: string
  filterInput: TreatmentListFilter
}

type ReturnData = {
  activeTreatments: DoctorPatientTreatment[]
  deletedTreatments: DoctorPatientTreatment[]
  hasAnyTreatment: boolean
  hasFilterResult: boolean
  hasActiveTreatment: boolean
  hasDeletedTreatment: boolean
  loading: boolean
  refetch: (
    variables?: Partial<getDoctorPatientTreatmentsVariables> | undefined
  ) => Promise<ApolloQueryResult<getDoctorPatientTreatments>>
}

export const useDoctorPatientTreatmentsData = ({
  skip,
  patientId: overridePatientId,
  filterInput,
}: Props): ReturnData => {
  const selectedLanguage = useSelectedLanguage()
  const { data: { me } = {} } = useMe()

  const { patientId: currentPatientId } = useParams<DoctorPatientRouteParams>()
  const patientId = overridePatientId || currentPatientId || ''

  const { data, loading, refetch } = useQuery<
    getDoctorPatientTreatments,
    getDoctorPatientTreatmentsVariables
  >(GET_DOCTOR_PATIENT_TREATMENTS, {
    variables: { patientId },
    skip: !patientId || skip,
    fetchPolicy: 'network-only',
  })

  const treatments = data?.getPatientProfile.treatments || []

  const sortedTreatments = [...treatments].sort((a, b) => {
    const aDate = a.doctorAppointments?.length
      ? a.doctorAppointments[0].proposedDate
      : null
    const bDate = b.doctorAppointments?.length
      ? b.doctorAppointments[0].proposedDate
      : null

    if (!bDate) {
      return aDate ? -1 : 0
    }
    if (!aDate) {
      return bDate ? 1 : 0
    }
    return dayjs(aDate).isBefore(bDate) ? -1 : 1
  })

  const filteredTreatments = sortedTreatments.filter((treatment) => {
    return (
      treatment.title[selectedLanguage]
        .toLowerCase()
        .includes(filterInput.searchTerm?.toLowerCase() ?? '') &&
      (filterInput.isOwn ? treatment.doctor.id === me?.profile.id : true)
    )
  })

  const activeTreatments = filteredTreatments.filter(
    (treatment) => treatment.status !== TreatmentStatus.Cancelled
  )

  const deletedTreatments = filteredTreatments.filter(
    (treatment) => treatment.status === TreatmentStatus.Cancelled
  )

  const hasAnyTreatment = !!treatments.length
  const hasFilterResult = !!filteredTreatments.length
  const hasActiveTreatment = !!activeTreatments.length
  const hasDeletedTreatment = !!deletedTreatments.length

  return {
    activeTreatments,
    deletedTreatments,
    hasAnyTreatment,
    hasFilterResult,
    hasActiveTreatment,
    hasDeletedTreatment,
    loading,
    refetch,
  }
}
